import { useState, useEffect } from 'react'
import { Users, BarChart3, Clock, CheckCircle, AlertTriangle, PauseCircle } from 'lucide-react'
import { useFilters } from '../contexts/FilterContext'
import { api } from '../services/api'
import styles from './Produtividade.module.css'

interface CorretorProdutividade {
  id: number
  nome: string
  imobiliaria: string
  leads_atribuidos: number
  leads_totais: number
  capacidade: number
  ocupacao: number
  leads_sem_contato: number
  leads_parados: number
  tempo_medio_resposta: number
  sla_cumprido: number
}

export function Produtividade() {
  const { queryParams, filtersVersion } = useFilters()
  const [corretores, setCorretores] = useState<CorretorProdutividade[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [kpis, setKpis] = useState({
    cargaMedia: 0,
    ocupacaoEquipe: 0,
    slaAtendimento: 0,
    atendimentosDia: 0,
    corretoresAtivos: 0,
    totalCorretores: 0,
    leadsSemContatoTotal: 0,
    leadsParadosTotal: 0,
    tempoMedioEquipe: 0,
  })

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      setError(null)
      try {
        // Carrega dados reais de produtividade da API
        const prodData = await api.getProdutividade(queryParams)

        // Mapeia dados dos corretores
        const produtividadeData: CorretorProdutividade[] = (prodData.corretores || []).map((c: any) => ({
          id: c.id,
          nome: c.nome,
          imobiliaria: c.imobiliaria || 'N/A',
          leads_atribuidos: c.leads_atribuidos || 0,
          leads_totais: c.leads_totais || 0,
          capacidade: c.capacidade || 80,
          ocupacao: c.ocupacao || 0,
          leads_sem_contato: c.leads_sem_contato || 0,
          leads_parados: c.leads_parados || 0,
          tempo_medio_resposta: c.tempo_medio_resposta || 0,
          sla_cumprido: c.sla_cumprido || 0,
        }))

        setCorretores(produtividadeData)

        // Usa KPIs calculados pela API
        setKpis({
          cargaMedia: prodData.kpis?.carga_media || 0,
          ocupacaoEquipe: prodData.kpis?.ocupacao_equipe || 0,
          slaAtendimento: prodData.kpis?.sla_atendimento || 0,
          atendimentosDia: prodData.kpis?.atendimentos_dia || 0,
          corretoresAtivos: prodData.kpis?.corretores_ativos || 0,
          totalCorretores: prodData.kpis?.total_corretores || 0,
          leadsSemContatoTotal: prodData.kpis?.leads_sem_contato_total || 0,
          leadsParadosTotal: prodData.kpis?.leads_parados_total || 0,
          tempoMedioEquipe: prodData.kpis?.tempo_medio_equipe || 0,
        })
      } catch (err) {
        setError('Erro ao carregar dados de produtividade')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [filtersVersion, queryParams])

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Carregando dados de produtividade...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Produtividade e Demanda</h1>
            <p className={styles.pageSubtitle}>Analise de capacidade e eficiencia da equipe comercial</p>
          </div>
        </div>
        <div className={styles.errorState}>
          <p>{error}</p>
          <p>Verifique se a API esta funcionando corretamente.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Produtividade e Demanda</h1>
          <p className={styles.pageSubtitle}>Analise de capacidade e eficiencia da equipe comercial</p>
        </div>
      </div>

      {/* KPIs de Produtividade */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Indicadores de Produtividade</h2>
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.primary}`}>
              <Users size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Carga Media Leads/Corretor</span>
              <span className={styles.kpiValue}>{kpis.cargaMedia}</span>
              <span className={styles.kpiMeta}>leads ativos por corretor</span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.secondary}`}>
              <BarChart3 size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Ocupacao Equipe Comercial</span>
              <span className={styles.kpiValue}>{kpis.ocupacaoEquipe}%</span>
              <span className={`${styles.kpiMeta} ${kpis.ocupacaoEquipe >= 80 && kpis.ocupacaoEquipe <= 90 ? styles.success : styles.warning}`}>
                {kpis.ocupacaoEquipe >= 80 && kpis.ocupacaoEquipe <= 90 ? 'Dentro da meta (80-90%)' : 'Fora da meta (80-90%)'}
              </span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${kpis.slaAtendimento >= 90 ? styles.success : kpis.slaAtendimento >= 50 ? styles.warning : styles.danger}`}>
              <Clock size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>SLA de Atendimento</span>
              <span className={styles.kpiValue}>{kpis.slaAtendimento.toFixed(1)}%</span>
              <span className={`${styles.kpiMeta} ${kpis.slaAtendimento >= 90 ? styles.success : kpis.slaAtendimento >= 50 ? styles.warning : styles.danger}`}>
                {kpis.slaAtendimento >= 90 ? 'Dentro da meta (>90%)' : kpis.slaAtendimento >= 50 ? 'Abaixo da meta (>90%)' : 'Critico - abaixo de 50%'}
              </span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.success}`}>
              <CheckCircle size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Atendimentos/Dia</span>
              <span className={styles.kpiValue}>{kpis.atendimentosDia}</span>
              <span className={styles.kpiMeta}>leads recebidos no periodo</span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${kpis.leadsSemContatoTotal > 0 ? styles.danger : styles.success}`}>
              <AlertTriangle size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Leads Sem Contato (+24h)</span>
              <span className={styles.kpiValue}>{kpis.leadsSemContatoTotal}</span>
              <span className={`${styles.kpiMeta} ${kpis.leadsSemContatoTotal > 0 ? styles.danger : styles.success}`}>
                {kpis.leadsSemContatoTotal > 0 ? 'Requer atencao imediata' : 'Todos contatados'}
              </span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${kpis.leadsParadosTotal > 0 ? styles.warning : styles.success}`}>
              <PauseCircle size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Leads Parados (+7 dias)</span>
              <span className={styles.kpiValue}>{kpis.leadsParadosTotal}</span>
              <span className={`${styles.kpiMeta} ${kpis.leadsParadosTotal > 0 ? styles.warning : styles.success}`}>
                {kpis.leadsParadosTotal > 0 ? 'Sem movimentacao ha mais de 7 dias' : 'Todos em andamento'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Resumo da Equipe */}
      <section className={styles.section}>
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryIcon}>
              <Users size={32} />
            </div>
            <div className={styles.summaryContent}>
              <span className={styles.summaryLabel}>Corretores Ativos</span>
              <span className={styles.summaryValue}>
                {kpis.corretoresAtivos} <span className={styles.summaryTotal}>de {kpis.totalCorretores} totais</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Tabela de Distribuicao de Carga */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Distribuicao de Carga por Corretor</h2>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Corretor</th>
                <th>Imobiliaria</th>
                <th>Leads Ativos</th>
                <th>Capacidade</th>
                <th>Ocupacao</th>
                <th>Sem Contato</th>
                <th>Parados</th>
                <th>Tempo Medio</th>
                <th>SLA</th>
              </tr>
            </thead>
            <tbody>
              {corretores.map((corretor) => (
                <tr key={corretor.id}>
                  <td className={styles.nameCell}>{corretor.nome}</td>
                  <td>{corretor.imobiliaria}</td>
                  <td>{corretor.leads_atribuidos}</td>
                  <td>{corretor.capacidade}</td>
                  <td>
                    <div className={styles.ocupacaoCell}>
                      <div className={styles.ocupacaoBar}>
                        <div
                          className={`${styles.ocupacaoFill} ${
                            corretor.ocupacao > 90 ? styles.danger :
                            corretor.ocupacao > 80 ? styles.success :
                            styles.warning
                          }`}
                          style={{ width: `${Math.min(100, corretor.ocupacao)}%` }}
                        />
                      </div>
                      <span>{corretor.ocupacao.toFixed(0)}%</span>
                    </div>
                  </td>
                  <td>
                    {corretor.leads_sem_contato > 0 ? (
                      <span className={styles.alertBadge}>
                        <AlertTriangle size={14} />
                        {corretor.leads_sem_contato}
                      </span>
                    ) : (
                      <span className={styles.successBadge}>
                        <CheckCircle size={14} />
                        0
                      </span>
                    )}
                  </td>
                  <td>
                    {corretor.leads_parados > 0 ? (
                      <span className={styles.alertBadge}>
                        <PauseCircle size={14} />
                        {corretor.leads_parados}
                      </span>
                    ) : (
                      <span className={styles.successBadge}>
                        <CheckCircle size={14} />
                        0
                      </span>
                    )}
                  </td>
                  <td>
                    {corretor.tempo_medio_resposta > 0
                      ? corretor.tempo_medio_resposta < 60
                        ? `${corretor.tempo_medio_resposta.toFixed(0)} min`
                        : `${(corretor.tempo_medio_resposta / 60).toFixed(1)} h`
                      : '-'
                    }
                  </td>
                  <td>
                    <span className={`${styles.slaBadge} ${
                      corretor.sla_cumprido >= 90 ? styles.success :
                      corretor.sla_cumprido >= 50 ? styles.warning :
                      styles.danger
                    }`}>
                      {corretor.sla_cumprido.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
